import { ActuadorData, Sensor, SensorData } from '../types';
import { DatosHistoricosResponse, VariableData, DatoHistoricoPoint } from '../types';

/**
 * Convierte datos de actuador al formato DatosHistoricosResponse
 * NOTA: La media móvil ahora se aplica en el backend antes del muestreo
 */
export const convertActuadorDataToHistoricos = (
  actuadorData: ActuadorData[]
): DatosHistoricosResponse => {
  const variables: VariableData[] = [];

  // Variables del actuador
  const actuadorVariables = [
    { key: 'fuerza', nombre: 'Fuerza', unidad: 'kN', sensorId: 'actuador' },
    { key: 'desplazamiento', nombre: 'Desplazamiento', unidad: 'mm', sensorId: 'actuador' },
    { key: 'rigidez', nombre: 'Rigidez', unidad: 'kN/mm', sensorId: 'actuador' },
  ];

  actuadorVariables.forEach(({ key, nombre, unidad, sensorId }) => {
    // Convertir a formato DatoHistoricoPoint con timestamps en segundos (string)
    // El timestamp está en segundos, y AdvancedLineChart espera strings cuando isTimeInSeconds=true
    const datos: DatoHistoricoPoint[] = actuadorData
      .map((item) => {
        const valor = item[key as keyof ActuadorData] as number | null;
        if (valor === null || valor === undefined) {
          return null;
        }
        return {
          timestamp: item.timestamp.toString(), // Mantener como string para AdvancedLineChart
          valor: valor,
          calidad: null,
        } as DatoHistoricoPoint;
      })
      .filter((d): d is DatoHistoricoPoint => d !== null);

    if (datos.length > 0) {
      variables.push({
        variable_id: key,
        variable_nombre: nombre,
        variable_unidad: unidad,
        sensor_id: sensorId,
        sensor_nombre: 'Actuador',
        datos: datos,
      });
    }
  });

  // Calcular fechas de inicio y fin (en segundos como string)
  const timestamps = actuadorData.map((d) => d.timestamp).filter((t) => t !== null && t !== undefined);
  const minTimestamp = timestamps.length > 0 ? Math.min(...timestamps) : 0;
  const maxTimestamp = timestamps.length > 0 ? Math.max(...timestamps) : 0;

  return {
    variables,
    fecha_inicio: minTimestamp.toString(),
    fecha_fin: maxTimestamp.toString(),
  };
};

/**
 * Convierte datos de sensores al formato DatosHistoricosResponse
 * NOTA: La media móvil ahora se aplica en el backend antes del muestreo
 */
export const convertSensorDataToHistoricos = (
  sensores: Sensor[],
  datosPorSensor: Record<string, SensorData[]>
): DatosHistoricosResponse => {
  const variables: VariableData[] = [];

  sensores.forEach((sensor) => {
    const sensorData = datosPorSensor[sensor.id] || [];
    
    if (sensorData.length === 0) {
      return;
    }

    // Si el sensor tiene variables múltiples (FOS agrupado)
    if (sensor.variables && sensor.variables.length > 0) {
      // Procesar cada variable por separado
      sensor.variables.forEach((variable) => {
        // Filtrar datos por variable_id
        const datosVariable = sensorData.filter(
          d => d.variable_id === variable.id
        );
        
        if (datosVariable.length === 0) {
          return;
        }

        // Convertir a formato DatoHistoricoPoint
        const datos: DatoHistoricoPoint[] = datosVariable
          .map((item) => {
            if (item.valor === null || item.valor === undefined) {
              return null;
            }
            return {
              timestamp: item.timestamp.toString(),
              valor: item.valor,
              calidad: null,
            } as DatoHistoricoPoint;
          })
          .filter((d): d is DatoHistoricoPoint => d !== null);

        if (datos.length > 0) {
          variables.push({
            variable_id: `sensor_${sensor.id}_var_${variable.id}`,
            variable_nombre: variable.nombre_completo || variable.nombre_variable,
            variable_unidad: variable.unidades || '',
            sensor_id: sensor.id,
            sensor_nombre: sensor.nombre,
            datos: datos,
          });
        }
      });
    } else {
      // Sensor normal sin variables múltiples
      // Convertir a formato DatoHistoricoPoint con timestamps en segundos (string)
      // El timestamp está en segundos, y AdvancedLineChart espera strings cuando isTimeInSeconds=true
      const datos: DatoHistoricoPoint[] = sensorData
        .map((item) => {
          if (item.valor === null || item.valor === undefined) {
            return null;
          }
          return {
            timestamp: item.timestamp.toString(), // Mantener como string para AdvancedLineChart
            valor: item.valor,
            calidad: null,
          } as DatoHistoricoPoint;
        })
        .filter((d): d is DatoHistoricoPoint => d !== null);

      if (datos.length > 0) {
        variables.push({
          variable_id: `sensor_${sensor.id}`,
          variable_nombre: sensor.nombre || `Sensor ${sensor.id.slice(0, 8)}`,
          variable_unidad: sensor.unidades || '',
          sensor_id: sensor.id,
          sensor_nombre: sensor.nombre,
          datos: datos,
        });
      }
    }
    
    // Para sensores LVDT, agregar desplazamiento si está disponible
    const tipoFinal = sensor.tipo_manual || sensor.tipo;
    if (tipoFinal === 'lvdt') {
      const datosDesplazamiento: DatoHistoricoPoint[] = sensorData
        .map((item) => {
          if (item.desplazamiento === null || item.desplazamiento === undefined) {
            return null;
          }
          return {
            timestamp: item.timestamp.toString(),
            valor: item.desplazamiento,
            calidad: null,
          } as DatoHistoricoPoint;
        })
        .filter((d): d is DatoHistoricoPoint => d !== null);
      
      if (datosDesplazamiento.length > 0) {
        variables.push({
          variable_id: `sensor_${sensor.id}_desplazamiento`,
          variable_nombre: `${sensor.nombre} - Desplazamiento`,
          variable_unidad: 'mm',
          sensor_id: sensor.id,
          sensor_nombre: sensor.nombre,
          datos: datosDesplazamiento,
        });
      }
    }
    
    // Para sensores FOS, agregar strain_calculado y temperature_calculado si están disponibles
    if (tipoFinal === 'fibra_optica') {
      // Agregar strain calculado
      const datosStrain: DatoHistoricoPoint[] = sensorData
        .map((item) => {
          if (item.strain_calculado === null || item.strain_calculado === undefined) {
            return null;
          }
          return {
            timestamp: item.timestamp.toString(),
            valor: item.strain_calculado,
            calidad: null,
          } as DatoHistoricoPoint;
        })
        .filter((d): d is DatoHistoricoPoint => d !== null);
      
      if (datosStrain.length > 0) {
        variables.push({
          variable_id: `sensor_${sensor.id}_strain_calculado`,
          variable_nombre: `${sensor.nombre} - Strain Calculado`,
          variable_unidad: 'με',
          sensor_id: sensor.id,
          sensor_nombre: sensor.nombre,
          datos: datosStrain,
        });
      }
      
      // Agregar temperature calculado
      const datosTemperature: DatoHistoricoPoint[] = sensorData
        .map((item) => {
          if (item.temperature_calculado === null || item.temperature_calculado === undefined) {
            return null;
          }
          return {
            timestamp: item.timestamp.toString(),
            valor: item.temperature_calculado,
            calidad: null,
          } as DatoHistoricoPoint;
        })
        .filter((d): d is DatoHistoricoPoint => d !== null);
      
      if (datosTemperature.length > 0) {
        variables.push({
          variable_id: `sensor_${sensor.id}_temperature_calculado`,
          variable_nombre: `${sensor.nombre} - Temperatura Calculada`,
          variable_unidad: '°C',
          sensor_id: sensor.id,
          sensor_nombre: sensor.nombre,
          datos: datosTemperature,
        });
      }
    }
  });

  // Calcular fechas de inicio y fin (en segundos como string)
  const allTimestamps: number[] = [];
  Object.values(datosPorSensor).forEach((data) => {
    data.forEach((d) => {
      if (d.timestamp) {
        allTimestamps.push(d.timestamp);
      }
    });
  });

  const minTimestamp = allTimestamps.length > 0 ? Math.min(...allTimestamps) : 0;
  const maxTimestamp = allTimestamps.length > 0 ? Math.max(...allTimestamps) : 0;

  return {
    variables,
    fecha_inicio: minTimestamp.toString(),
    fecha_fin: maxTimestamp.toString(),
  };
};

/**
 * Combina datos de actuador y sensores en un solo DatosHistoricosResponse
 * NOTA: La media móvil ahora se aplica en el backend antes del muestreo
 */
export const combineEnsayoDataToHistoricos = (
  actuadorData: ActuadorData[],
  sensores: Sensor[],
  datosPorSensor: Record<string, SensorData[]>
): DatosHistoricosResponse => {
  const actuadorHistoricos = convertActuadorDataToHistoricos(actuadorData);
  const sensoresHistoricos = convertSensorDataToHistoricos(sensores, datosPorSensor);

  return {
    variables: [...actuadorHistoricos.variables, ...sensoresHistoricos.variables],
    fecha_inicio: actuadorHistoricos.variables.length > 0 
      ? actuadorHistoricos.fecha_inicio 
      : sensoresHistoricos.fecha_inicio,
    fecha_fin: actuadorHistoricos.variables.length > 0
      ? actuadorHistoricos.fecha_fin
      : sensoresHistoricos.fecha_fin,
  };
};

